import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { useAuthStore } from '@/stores/auth';
import { computed, ref } from 'vue';
import { useScriptSureApi } from './useScriptSureApi';

/**
 * Strip non-numeric characters from phone number and truncate to 10 digits
 * @param {string} phone - The phone number
 * @returns {string} Formatted phone number
 */
const formatPhoneForScriptSure = (phone) => {
    if (!phone) return '';
    return phone.replace(/\D/g, '').slice(0, 10);
};

/**
 * Map patient gender to ScriptSure single character format
 * M = Male, F = Female, O = Other, U = Unknown
 * @param {string} gender - The FHIR gender string
 * @returns {string} Single character gender code
 */
const mapGenderToScriptSure = (gender) => {
    const genderMap = {
        male: 'M',
        female: 'F',
        other: 'O',
        unknown: 'U',
    };
    return genderMap[gender?.toLowerCase()] || 'U';
};

/**
 * Remove empty string, null, and undefined values from an object
 * @param {Object} obj - The object to clean
 * @returns {Object} Object with empty values removed
 */
const removeEmptyValues = (obj) => {
    return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== '' && value !== null && value !== undefined));
};

/**
 * Validate patient data for ScriptSure registration
 * @param {Object} patient - The Patient resource
 * @returns {string[]} Array of missing/invalid field names (empty if valid)
 */
const validatePatientForScriptSure = (patient) => {
    const errors = [];

    if (!patient.firstName?.trim()) {
        errors.push('First name');
    } else if (patient.firstName.length > 35) {
        errors.push('First name must be 35 characters or less');
    }

    if (!patient.lastName?.trim()) {
        errors.push('Last name');
    } else if (patient.lastName.length > 35) {
        errors.push('Last name must be 35 characters or less');
    }

    if (!patient.birthDate) {
        errors.push('Date of birth');
    }

    if (!patient.gender || patient.gender === 'unknown') {
        errors.push('Gender');
    }

    const defaultAddress = patient.defaultAddress;
    if (!defaultAddress) {
        errors.push('Address');
    } else {
        if (!defaultAddress.line1?.trim()) {
            errors.push('Address line 1');
        } else if (defaultAddress.line1.length > 35) {
            errors.push('Address line 1 must be 35 characters or less');
        }

        if (!defaultAddress.city?.trim()) {
            errors.push('City');
        } else if (defaultAddress.city.length > 35) {
            errors.push('City must be 35 characters or less');
        }

        if (!defaultAddress.state?.trim()) {
            errors.push('State');
        } else if (defaultAddress.state.length > 2) {
            errors.push('State must be 2 characters or less');
        }

        if (!defaultAddress.postalCode?.trim()) {
            errors.push('Zip code');
        } else if (defaultAddress.postalCode.replace(/\D/g, '').length > 9) {
            errors.push('Zip code must be 9 digits or less');
        }
    }

    const homePhone = formatPhoneForScriptSure(patient.homePhoneValue);
    const workPhone = formatPhoneForScriptSure(patient.workPhoneValue);
    const cellPhone = formatPhoneForScriptSure(patient.mobilePhoneValue);

    if (!homePhone && !workPhone && !cellPhone) {
        errors.push('Home, work, or cell phone number');
    }

    return errors;
};

/**
 * Build sign-in data object from a Patient resource and prescriber context
 * @param {Object} patient - The Patient resource
 * @param {Object} prescriberContext - { practiceId, prescriberId, isActingOnBehalf }
 * @returns {Object} The sign-in payload for the ScriptSure API
 */
const buildSignInDataForPatient = (patient, prescriberContext) => {
    const defaultAddress = patient.defaultAddress;

    const homePhone = formatPhoneForScriptSure(patient.homePhoneValue);
    const workPhone = formatPhoneForScriptSure(patient.workPhoneValue);
    const cellPhone = formatPhoneForScriptSure(patient.mobilePhoneValue);

    const officialName = patient.name?.find((n) => n.use === 'official');
    const suffix = officialName?.suffix?.[0] || '';

    const data = {
        // Prescriber context
        practiceId: prescriberContext.practiceId,
        prescriberId: prescriberContext.prescriberId,
        isActingOnBehalf: prescriberContext.isActingOnBehalf,

        // Required patient fields
        firstName: patient.firstName?.substring(0, 35),
        lastName: patient.lastName?.substring(0, 35),
        dob: patient.birthDate?.substring(0, 10),
        gender: mapGenderToScriptSure(patient.gender),
        addressLine1: defaultAddress?.line1?.substring(0, 35),
        city: defaultAddress?.city?.substring(0, 35),
        zip: defaultAddress?.postalCode?.replace(/\D/g, '').substring(0, 9),
        state: defaultAddress?.state?.substring(0, 2),
        home: homePhone,
        work: workPhone,
        cell: cellPhone,
        consent: false,
        patientStatusId: patient.active === false ? 6 : 0,

        // Optional patient fields
        patientIdExternal: patient.id?.replaceAll('-', ''),
        middleName: patient.middleName?.substring(0, 35),
        suffix: suffix?.substring(0, 35),
        ssn: patient.ssn?.replace(/\D/g, ''),
        addressLine2: defaultAddress?.line2?.substring(0, 35),
        email: patient.primaryEmailValue?.substring(0, 80),
    };

    return removeEmptyValues(data);
};

/**
 * Composable for syncing clinical data (allergies, diagnoses, and medications) to ScriptSure.
 * Provides shared functionality for AllergiesWidget, DiagnosisListWidget, MedicationsListWidget, and BnMedicationsCard.
 */
export function useScriptSureClinicalSync() {
    const authStore = useAuthStore();
    const { syncAllergy, updateAllergy, syncDiagnosis, updateDiagnosis, syncMedications, signIn, searchAllergen, updatePatient } = useScriptSureApi();

    // Track resources currently being synced to prevent duplicate syncs
    const allergiesBeingSynced = ref(new Set());
    const diagnosesBeingSynced = ref(new Set());

    // In-memory cache of scriptSurePatientId by FHIR patient ID
    const patientIdCache = new Map();

    // Stores the scriptSureUrl from the most recent new-patient registration signIn response,
    // so connectToScriptSure can skip a redundant second signIn call for new patients.
    let lastRegistrationUrl = null;

    // Cache for acting-on-behalf prescriber ID
    const actingOnBehalfPrescriberIdCache = ref(null);
    const actingOnBehalfLoaded = ref(false);

    // Check if ScriptSure is enabled for the account
    const isScriptSureEnabled = computed(() => authStore.account?.scriptSureEnabled);

    // Prescriber context computed from auth store
    const practiceId = computed(() => authStore.brand?.location?.scriptSurePracticeId);
    const isProvider = computed(() => authStore.user?.user?.isProvider);
    const ownPrescriberId = computed(() => authStore.practitioner?.scriptSurePrescriberId);
    const actingOnBehalfOf = computed(() => authStore.practitioner?.scriptSureActingOnBehalfOf);
    const isActingOnBehalf = computed(() => !isProvider.value && !!actingOnBehalfOf.value?.id);

    const prescriberId = computed(() => {
        if (isProvider.value && ownPrescriberId.value) {
            return ownPrescriberId.value;
        }
        if (isActingOnBehalf.value && actingOnBehalfPrescriberIdCache.value) {
            return actingOnBehalfPrescriberIdCache.value;
        }
        return null;
    });

    /**
     * Check if ScriptSure credentials are available (account enabled + user has prescriber access)
     * @returns {boolean}
     */
    const hasScriptSureCredentials = computed(() => {
        return isScriptSureEnabled.value && !!practiceId.value && !!prescriberId.value;
    });

    /**
     * Fetch the acting-on-behalf practitioner's prescriber ID (cached after first call)
     */
    const loadActingOnBehalfPrescriberId = async () => {
        if (actingOnBehalfLoaded.value) return;
        if (!isActingOnBehalf.value || !actingOnBehalfOf.value?.id) {
            actingOnBehalfLoaded.value = true;
            return;
        }

        try {
            const response = await $httpFhirApi.get(`Practitioner/${actingOnBehalfOf.value.id}?_elements=scriptSurePrescriberId`);
            if (response.data?.scriptSurePrescriberId) {
                actingOnBehalfPrescriberIdCache.value = response.data.scriptSurePrescriberId;
            }
        } catch (err) {
            console.error('Failed to fetch acting-on-behalf practitioner:', err);
        } finally {
            actingOnBehalfLoaded.value = true;
        }
    };

    /**
     * Ensure a patient is registered in ScriptSure. Returns the scriptSurePatientId if successful.
     * - Returns the cached/existing ID if already registered
     * - Auto-registers the patient if not yet registered and data is valid
     * - Returns { missingFields: [...] } if patient data is incomplete
     * - Returns null if ScriptSure is not configured or credentials are missing
     *
     * @param {Object} patient - The full Patient FHIR resource
     * @returns {Promise<string|{missingFields: string[]}|null>} scriptSurePatientId, missing fields object, or null
     */
    const ensureScriptSurePatient = async (patient) => {
        if (!patient?.id) return null;

        // Ensure acting-on-behalf prescriber ID is loaded
        if (isActingOnBehalf.value && !actingOnBehalfLoaded.value) {
            await loadActingOnBehalfPrescriberId();
        }

        // Check prerequisites
        if (!isScriptSureEnabled.value || !practiceId.value || !prescriberId.value) {
            return null;
        }

        // Check in-memory cache
        const cached = patientIdCache.get(patient.id);
        if (cached) return cached;

        // Check on the patient object itself
        if (patient.scriptSurePatientId) {
            patientIdCache.set(patient.id, patient.scriptSurePatientId);
            return patient.scriptSurePatientId;
        }

        // Fetch from FHIR in case it was set by another session/component
        const existingId = await getScriptSurePatientId(patient.id);
        if (existingId) {
            patientIdCache.set(patient.id, existingId);
            return existingId;
        }

        // Patient is not registered — validate data
        const validationErrors = validatePatientForScriptSure(patient);
        if (validationErrors.length > 0) {
            return { missingFields: validationErrors };
        }

        // Build sign-in payload and register
        try {
            const signInData = buildSignInDataForPatient(patient, {
                practiceId: practiceId.value,
                prescriberId: prescriberId.value,
                isActingOnBehalf: isActingOnBehalf.value,
            });

            const response = await signIn(signInData);

            if (response?.scriptSurePatientId) {
                const scriptSurePatientId = String(response.scriptSurePatientId);

                // Patch back to FHIR
                await $httpFhirApi.patch(`Patient/${patient.id}`, [{ op: 'add', path: '/scriptSurePatientId', value: scriptSurePatientId }]);

                // Update local object and cache
                patient.scriptSurePatientId = scriptSurePatientId;
                patientIdCache.set(patient.id, scriptSurePatientId);

                // Stash the URL so callers can skip a redundant signIn for new registrations
                lastRegistrationUrl = response.scriptSureUrl || null;

                return scriptSurePatientId;
            }

            return null;
        } catch (err) {
            console.error('Failed to auto-register patient in ScriptSure:', err);
            return null;
        }
    };

    // "No Known Allergies" SNOMED code
    const NO_KNOWN_ALLERGY_CODE = '716186003';

    /**
     * Map allergy category to ScriptSure allergyType
     * If the allergy has FDB data from ScriptSure allergen search, use that directly.
     * Otherwise fall back to heuristic mapping:
     * 0=Singular Chemical, 1=Brand, 2=Generic, 3=Drug Class, 4=RxNorm, 5=NDC, 100=No Known Allergies
     * @param {Object} allergy - The AllergyIntolerance resource
     * @returns {number} The ScriptSure allergyType code
     */
    /**
     * Parse FDB coding stored as "allergyId|allergyType" in the code field
     * @param {Object} fdbCoding - The coding object with system http://fhir.bestnotes.com/scriptsure/fdb
     * @returns {{ allergyId: string, allergyType: number }|null}
     */
    const parseFdbCoding = (fdbCoding) => {
        if (!fdbCoding?.code) return null;
        const parts = fdbCoding.code.split('|');
        if (parts.length !== 2) return null;
        return { allergyId: parts[0], allergyType: parseInt(parts[1]) };
    };

    const mapAllergyTypeToScriptSure = (allergy) => {
        // Use FDB allergyType if available (set when user selects from ScriptSure search)
        const fdbCoding = allergy.code?.coding?.find((c) => c.system === 'http://fhir.bestnotes.com/scriptsure/fdb');
        const fdbData = parseFdbCoding(fdbCoding);
        if (fdbData) {
            return fdbData.allergyType;
        }

        const rxnormCode = allergy.code?.coding?.find((c) => c.system === 'http://www.nlm.nih.gov/research/umls/rxnorm')?.code;
        if (rxnormCode) {
            return 4; // RxNorm
        }
        if (allergy.category?.[0] === 'medication') {
            return 2; // Generic
        }
        return 0; // Singular Chemical
    };

    /**
     * Map allergy category to ScriptSure adverseEventCode (SNOMED codes)
     * Drug allergy: 416098002, Food allergy: 414285001, Other: 420134006
     * @param {string} category - The allergy category
     * @returns {string} The SNOMED adverse event code
     */
    const mapAdverseEventCodeToScriptSure = (category) => {
        const codeMap = {
            medication: '416098002', // Drug allergy
            food: '414285001', // Food allergy
            environment: '420134006', // Allergy to substance
            biologic: '420134006', // Allergy to substance
        };
        return codeMap[category] || '420134006';
    };

    /**
     * Map diagnosis code system to ScriptSure codingSystem
     * 0=Unknown, 1=ICD9, 2=ICD10
     * @param {string} system - The FHIR code system URL
     * @returns {number} The ScriptSure codingSystem code
     */
    const mapCodingSystemToScriptSure = (system) => {
        const systemLower = system?.toLowerCase() || '';
        if (systemLower.includes('icd10') || systemLower.includes('icd-10')) {
            return 2; // ICD10
        }
        if (systemLower.includes('icd9') || systemLower.includes('icd-9')) {
            return 1; // ICD9
        }
        // DSM-5 codes should also use ICD-10 format
        if (systemLower.includes('dsm5') || systemLower.includes('dsm')) {
            return 2; // ICD10
        }
        return 0; // Unknown
    };

    /**
     * Sync an allergy to ScriptSure and save the ScriptSure ID back to FHIR
     * @param {Object} allergy - The AllergyIntolerance resource
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<boolean>} Whether the sync was successful
     */
    const syncAllergyToScriptSure = async (allergy, scriptSurePatientId) => {
        const allergyName = allergy.code?.text || allergy.code?.coding?.[0]?.display;

        if (!scriptSurePatientId || !allergyName) {
            return false;
        }

        // Skip "no known allergies" entries
        if (allergy.code?.coding?.[0]?.code === NO_KNOWN_ALLERGY_CODE) {
            return false;
        }

        try {
            const rxnormCode = allergy.code?.coding?.find((c) => c.system === 'http://www.nlm.nih.gov/research/umls/rxnorm')?.code;
            const ndcCode = allergy.code?.coding?.find((c) => c.system === 'http://hl7.org/fhir/sid/ndc')?.code;
            const fdbCoding = allergy.code?.coding?.find((c) => c.system === 'http://fhir.bestnotes.com/scriptsure/fdb');
            const fdbData = parseFdbCoding(fdbCoding);

            const allergyData = {
                patientId: parseInt(scriptSurePatientId),
                allergyId: fdbData?.allergyId || rxnormCode || allergyName,
                allergyType: mapAllergyTypeToScriptSure(allergy),
                adverseEventCode: mapAdverseEventCodeToScriptSure(allergy.category?.[0]),
                name: allergyName,
                archive: allergy.clinicalStatus?.coding?.[0]?.code === 'active' ? 0 : 1,
            };

            // Add drug codes for drug-allergy interaction checking
            if (rxnormCode) {
                allergyData.rxnorm = rxnormCode;
            }
            if (ndcCode) {
                allergyData.ndc = ndcCode;
            }
            if (allergy.onset?.string) {
                // ScriptSure expects ISO 8601 datetime format
                const onsetDate = allergy.onset.string;
                if (onsetDate.includes('-') && onsetDate.length === 10) {
                    allergyData.onsetDate = `${onsetDate}T12:00:00+00:00`;
                } else if (!onsetDate.includes('T')) {
                    allergyData.onsetDate = `${onsetDate}T12:00:00+00:00`;
                } else {
                    allergyData.onsetDate = onsetDate;
                }
            }
            if (allergy.reaction?.[0]?.manifestation?.[0]?.text) {
                allergyData.comment = allergy.reaction[0].manifestation[0].text;
            }

            let response;
            if (allergy.scriptSureAllergyId) {
                // Update existing allergy in ScriptSure
                allergyData.id = parseInt(allergy.scriptSureAllergyId);
                response = await updateAllergy(allergyData);
            } else {
                // Create new allergy in ScriptSure
                response = await syncAllergy(allergyData);
            }

            // Save the ScriptSure allergy ID back to FHIR if this is a new sync
            const scriptSureAllergyIdFromResponse = response?.allergy?.id;
            if (scriptSureAllergyIdFromResponse && !allergy.scriptSureAllergyId) {
                await $httpFhirApi.patch(`AllergyIntolerance/${allergy.id}`, [{ op: 'add', path: '/scriptSureAllergyId', value: String(scriptSureAllergyIdFromResponse) }]);
                // Update local object so subsequent syncs know it's already synced
                allergy.scriptSureAllergyId = String(scriptSureAllergyIdFromResponse);
            }

            return true;
        } catch (err) {
            console.error('Failed to sync allergy to ScriptSure:', err);
            return false;
        }
    };

    /**
     * Sync a diagnosis to ScriptSure and save the ScriptSure ID back to FHIR
     * @param {Object} diagnosis - The Condition resource
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<boolean>} Whether the sync was successful
     */
    const syncDiagnosisToScriptSure = async (diagnosis, scriptSurePatientId) => {
        const diagnosisCode = diagnosis.code?.coding?.[0]?.code;
        const diagnosisName = diagnosis.code?.text || diagnosis.code?.coding?.[0]?.display;

        if (!scriptSurePatientId || !diagnosisCode || !diagnosisName) {
            return false;
        }

        try {
            const clinicalStatusCode = diagnosis.clinicalStatus?.coding?.[0]?.code || 'active';
            const isActive = ['active', 'recurrence', 'relapse', 'remission'].includes(clinicalStatusCode);

            // Map verificationStatus to ScriptSure diagnosisTypeId
            // 1 = Initial Diagnosis, 2 = Final Diagnosis
            const verificationCode = diagnosis.verificationStatus?.coding?.[0]?.code;
            const diagnosisTypeId = verificationCode === 'confirmed' ? 2 : 1;

            const diagnosisData = {
                patientId: parseInt(scriptSurePatientId),
                conceptId: diagnosisCode,
                codingSystem: mapCodingSystemToScriptSure(diagnosis.code?.coding?.[0]?.system),
                name: diagnosisName,
                archive: isActive ? 0 : 1,
                diagnosisTypeId,
                short: diagnosisCode,
                long: diagnosisName,
            };

            // Map FHIR Condition.recordedDate to ScriptSure startDate
            if (diagnosis.recordedDate) {
                diagnosisData.startDate = diagnosis.recordedDate;
            }

            // Add comment from notes if available
            const noteText = diagnosis.note?.[0]?.text;
            if (noteText) {
                diagnosisData.comment = noteText;
            }

            // Add onset date as comment supplement if present and no note
            if (!noteText && diagnosis.onset?.dateTime) {
                diagnosisData.comment = `Onset: ${diagnosis.onset.dateTime}`;
            }

            // Add recorder info if available
            if (diagnosis.recorder?.display) {
                diagnosisData.doctorName = diagnosis.recorder.display;
            }

            let response;
            if (diagnosis.scriptSureDiagnosisId) {
                // Update existing diagnosis in ScriptSure
                diagnosisData.id = parseInt(diagnosis.scriptSureDiagnosisId);
                // Send the stored encounter ID so ScriptSure can match the record
                if (diagnosis.scriptSureEncounterId) {
                    diagnosisData.encounterId = parseInt(diagnosis.scriptSureEncounterId);
                }
                response = await updateDiagnosis(diagnosisData);
            } else {
                // Create new diagnosis in ScriptSure
                response = await syncDiagnosis(diagnosisData);
            }

            // Save the ScriptSure diagnosis ID and encounter ID back to FHIR if this is a new sync
            const scriptSureDiagnosisIdFromResponse = response?.diagnosis?.id;
            const scriptSureEncounterIdFromResponse = response?.encounterId;
            if (scriptSureDiagnosisIdFromResponse && !diagnosis.scriptSureDiagnosisId) {
                const patchOps = [{ op: 'add', path: '/scriptSureDiagnosisId', value: String(scriptSureDiagnosisIdFromResponse) }];
                if (scriptSureEncounterIdFromResponse) {
                    patchOps.push({ op: 'add', path: '/scriptSureEncounterId', value: String(scriptSureEncounterIdFromResponse) });
                }
                await $httpFhirApi.patch(`Condition/${diagnosis.id}`, patchOps);
                diagnosis.scriptSureDiagnosisId = String(scriptSureDiagnosisIdFromResponse);
                if (scriptSureEncounterIdFromResponse) {
                    diagnosis.scriptSureEncounterId = String(scriptSureEncounterIdFromResponse);
                }
            }

            return true;
        } catch (err) {
            console.error('Failed to sync diagnosis to ScriptSure:', err);
            return false;
        }
    };

    /**
     * Sync a medication to ScriptSure and save the ScriptSure prescription ID back to FHIR
     * @param {Object} medication - The MedicationRequest resource
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<{success: boolean, drugNotFound?: boolean}>} Result with drugNotFound flag when the RxNorm code wasn't found in ScriptSure's FDB
     */
    const syncMedicationToScriptSure = async (medication, scriptSurePatientId) => {
        const drugName = medication.medication?.CodeableConcept?.text;

        if (!scriptSurePatientId || !drugName) {
            return { success: false };
        }

        try {
            // Collect all RxNorm codes — ScriptSure needs a specific clinical drug code (SCD/SBD),
            // not just the ingredient-level code. Send all so the backend can try each one.
            const rxnormCodes =
                medication.medication?.CodeableConcept?.coding
                    ?.filter((c) => c.system === 'http://www.nlm.nih.gov/research/umls/rxnorm')
                    ?.map((c) => c.code)
                    ?.filter(Boolean) || [];

            // Also grab NDC code as a fallback for drug lookup
            const ndcCode = medication.medication?.CodeableConcept?.coding?.find((c) => c.system === 'http://hl7.org/fhir/sid/ndc')?.code;

            // Get the most recent reconciliation date from the array
            const lastReconciliation = medication.reconciliation?.length ? medication.reconciliation[medication.reconciliation.length - 1] : null;

            // Determine if the medication is archived:
            // - FHIR status of stopped/cancelled/completed, OR
            // - effective.end date is today or in the past (how archival works in the UI)
            const inactiveStatuses = ['stopped', 'cancelled', 'completed'];
            const hasInactiveStatus = inactiveStatuses.includes(medication.status);
            const hasEndedEffective = (() => {
                if (!medication.effective?.end) return false;
                const endDate = new Date(medication.effective.end + 'T23:59:59');
                return endDate <= new Date();
            })();
            const isArchived = hasInactiveStatus || hasEndedEffective;

            const medicationData = {
                drugName,
                status: medication.status || 'active',
                archive: isArchived,
                reconcileDate: lastReconciliation?.date || new Date().toISOString(),
            };

            // Add ScriptSure prescription ID and encounter ID if this is an update
            if (medication.scriptSurePrescriptionId) {
                medicationData.scriptSurePrescriptionId = medication.scriptSurePrescriptionId;
            }
            if (medication.scriptSureEncounterId) {
                medicationData.scriptSureEncounterId = medication.scriptSureEncounterId;
            }

            // Add drug codes — send all RxNorm codes so backend can try each
            if (rxnormCodes.length > 0) {
                medicationData.rxnormCodes = rxnormCodes;
            }
            if (ndcCode) {
                medicationData.ndc = ndcCode;
            }
            if (medication.dosageInstruction?.[0]?.text) {
                medicationData.dose = medication.dosageInstruction[0].text;
            }
            if (medication.dosageInstruction?.[0]?.timing?.code?.text) {
                medicationData.frequency = medication.dosageInstruction[0].timing.code.text;
            }
            if (medication.effective?.start) {
                medicationData.startDate = medication.effective.start;
            }
            if (medication.effective?.end) {
                medicationData.endDate = medication.effective.end;
            }

            // Ensure acting-on-behalf prescriber ID is loaded before accessing prescriberId
            if (isActingOnBehalf.value && !actingOnBehalfLoaded.value) {
                await loadActingOnBehalfPrescriberId();
            }

            const response = await syncMedications({
                patientId: parseInt(scriptSurePatientId),
                practiceId: practiceId.value,
                prescriberId: prescriberId.value,
                medications: [medicationData],
            });

            // Save the ScriptSure prescription ID and encounter ID back to FHIR if this is a new sync
            const result = response?.results?.[0];
            const encounterId = response?.encounterId;
            if (result?.prescriptionId && !medication.scriptSurePrescriptionId) {
                const patchOps = [{ op: 'add', path: '/scriptSurePrescriptionId', value: String(result.prescriptionId) }];
                if (encounterId) {
                    patchOps.push({ op: 'add', path: '/scriptSureEncounterId', value: String(encounterId) });
                }
                await $httpFhirApi.patch(`MedicationRequest/${medication.id}`, patchOps);
                // Update local object so subsequent syncs know it's already synced
                medication.scriptSurePrescriptionId = String(result.prescriptionId);
                if (encounterId) {
                    medication.scriptSureEncounterId = String(encounterId);
                }
            }

            return { success: true, drugNotFound: result?.drugNotFound || false };
        } catch (err) {
            console.error('Failed to sync medication to ScriptSure:', err);
            return { success: false };
        }
    };

    /**
     * @param {Object} options
     * @param {Object} options.resource - The FHIR resource (AllergyIntolerance or MedicationRequest)
     * @param {Object} [options.patient] - The Patient FHIR resource (used for ensureScriptSurePatient)
     * @param {string} [options.scriptSurePatientId] - Pre-resolved ScriptSure patient ID (takes priority)
     * @param {string} [options.patientId] - FHIR patient ID (fallback for getScriptSurePatientId)
     * @param {Function} options.syncFn - The sync function to call (e.g. syncAllergyToScriptSure)
     * @param {string} options.operationLabel - Label for error logging (e.g. 'allergy inactivation')
     * @param {Function} [options.onMissingFields] - Callback when ensureScriptSurePatient returns missing fields
     */
    const resolveAndSyncResource = async ({ resource, patient, scriptSurePatientId, patientId, syncFn, operationLabel, onMissingFields }) => {
        if (!isScriptSureEnabled.value || !resource?.id) return;

        const resolvedId = scriptSurePatientId || (patient ? await ensureScriptSurePatient(patient) : null) || (patientId ? await getScriptSurePatientId(patientId) : null);

        if (resolvedId && typeof resolvedId === 'object' && resolvedId.missingFields) {
            onMissingFields?.(resolvedId.missingFields);
            return;
        }

        if (typeof resolvedId === 'string' && resolvedId) {
            syncFn(resource, resolvedId).catch((err) => console.error(`Failed to sync ${operationLabel} to ScriptSure:`, err));
        }
    };

    /**
     * Handle syncing allergies after a FHIR save operation
     * Filters and syncs allergies that need to be synced to ScriptSure
     * @param {Array} allergies - Array of AllergyIntolerance resources
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<void>}
     */
    const handleAllergiesSaved = async (allergies, scriptSurePatientId) => {
        if (!isScriptSureEnabled.value || !scriptSurePatientId) {
            return;
        }

        const allergiesToSync = allergies.filter((allergy) => {
            // Must be an AllergyIntolerance resource
            if (allergy.resourceType !== 'AllergyIntolerance') return false;
            // Must have an id (saved to FHIR)
            if (!allergy.id) return false;
            // Must have allergen text or display
            if (!allergy.code?.text && !allergy.code?.coding?.[0]?.display) return false;
            // Skip "no known allergies" entries
            if (allergy.code?.coding?.[0]?.code === NO_KNOWN_ALLERGY_CODE) return false;
            // Skip if already being synced
            if (allergiesBeingSynced.value.has(allergy.id)) return false;
            return true;
        });

        for (const allergy of allergiesToSync) {
            allergiesBeingSynced.value.add(allergy.id);

            syncAllergyToScriptSure(allergy, scriptSurePatientId)
                .catch((err) => {
                    console.error('Background ScriptSure allergy sync failed:', err);
                })
                .finally(() => {
                    allergiesBeingSynced.value.delete(allergy.id);
                });
        }
    };

    /**
     * Handle syncing diagnoses after a FHIR save operation
     * Filters and syncs diagnoses that need to be synced to ScriptSure
     * @param {Array} diagnoses - Array of Condition resources
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<void>}
     */
    const handleDiagnosesSaved = async (diagnoses, scriptSurePatientId) => {
        if (!isScriptSureEnabled.value || !scriptSurePatientId) {
            return;
        }

        const diagnosesToSync = diagnoses.filter((diagnosis) => {
            // Must be a Condition resource
            if (diagnosis.resourceType !== 'Condition') return false;
            // Must have an id (saved to FHIR)
            if (!diagnosis.id) return false;
            // Must have diagnosis code
            if (!diagnosis.code?.coding?.[0]?.code) return false;
            // Skip if already being synced
            if (diagnosesBeingSynced.value.has(diagnosis.id)) return false;
            return true;
        });

        for (const diagnosis of diagnosesToSync) {
            diagnosesBeingSynced.value.add(diagnosis.id);

            syncDiagnosisToScriptSure(diagnosis, scriptSurePatientId)
                .catch((err) => {
                    console.error('Background ScriptSure diagnosis sync failed:', err);
                })
                .finally(() => {
                    diagnosesBeingSynced.value.delete(diagnosis.id);
                });
        }
    };

    /**
     * Fetch the scriptSurePatientId for a patient
     * @param {string} patientId - The FHIR Patient ID
     * @returns {Promise<string|null>} The ScriptSure patient ID or null
     */
    const getScriptSurePatientId = async (patientId) => {
        if (!isScriptSureEnabled.value || !patientId) {
            return null;
        }

        try {
            const response = await $httpFhirApi.get(`Patient/${patientId}?_elements=scriptSurePatientId`);
            return response.data?.scriptSurePatientId || null;
        } catch (err) {
            console.error('Failed to fetch scriptSurePatientId:', err);
            return null;
        }
    };

    /**
     * Sync patient demographics to ScriptSure
     * @param {Object} patient - The Patient FHIR resource (must have scriptSurePatientId to trigger sync)
     * @returns {Promise<boolean>} Whether the sync was attempted (false if skipped due to guards)
     */
    const syncPatientDemographics = async (patient) => {
        if (!isScriptSureEnabled.value || !patient?.scriptSurePatientId || patient.resourceType !== 'Patient') {
            return false;
        }

        if (isActingOnBehalf.value && !actingOnBehalfLoaded.value) {
            await loadActingOnBehalfPrescriberId();
        }

        if (!practiceId.value || !prescriberId.value) {
            return false;
        }

        try {
            const payload = buildSignInDataForPatient(patient, {
                practiceId: practiceId.value,
                prescriberId: prescriberId.value,
                isActingOnBehalf: isActingOnBehalf.value,
            });
            payload.scriptSurePatientId = patient.scriptSurePatientId;

            await updatePatient(payload);
            return true;
        } catch (err) {
            console.error('Failed to sync patient demographics to ScriptSure:', err);
            return false;
        }
    };

    return {
        // State
        isScriptSureEnabled,
        hasScriptSureCredentials,
        allergiesBeingSynced,
        diagnosesBeingSynced,

        // Constants
        NO_KNOWN_ALLERGY_CODE,

        // Mapping functions (exported for use in BnMedicationsCard bulk sync)
        mapAllergyTypeToScriptSure,
        mapAdverseEventCodeToScriptSure,
        mapCodingSystemToScriptSure,

        // Single resource sync functions
        syncAllergyToScriptSure,
        syncDiagnosisToScriptSure,
        syncMedicationToScriptSure,
        syncPatientDemographics,
        resolveAndSyncResource,

        // ScriptSure FDB allergen search
        searchAllergen,

        // Batch sync handlers for DataProvider @saved events
        handleAllergiesSaved,
        handleDiagnosesSaved,

        // Patient registration
        ensureScriptSurePatient,
        loadActingOnBehalfPrescriberId,
        consumeLastRegistrationUrl: () => {
            const url = lastRegistrationUrl;
            lastRegistrationUrl = null;
            return url;
        },

        // Utility functions
        getScriptSurePatientId,
        validatePatientForScriptSure: (patient) => validatePatientForScriptSure(patient),
        buildSignInDataForPatient: (patient) =>
            buildSignInDataForPatient(patient, {
                practiceId: practiceId.value,
                prescriberId: prescriberId.value,
                isActingOnBehalf: isActingOnBehalf.value,
            }),
        formatPhoneForScriptSure,
        mapGenderToScriptSure,
    };
}
